import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_KEY } from '@/components/utils/constants';

interface SharedLinkData {
  shareToken: string;
  shareUrl: string;
}

export const useCreateSharedLink = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sharedLink, setSharedLink] = useState<SharedLinkData | null>(null);

  const generateLink = async (
    itineraryId: string,
    password?: string,
    expiresAt?: string
  ): Promise<SharedLinkData | null> => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const payload = {
        itineraryId,
        ...(password && { password }),
        ...(expiresAt && { expiresAt })
      };

      console.log('[useCreateSharedLink] Calling edge function with payload:', payload);

      const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
      const { data, error: err } = await supabase.functions.invoke('create-shared-link', {
        body: JSON.stringify(payload),
      });

      console.log('[useCreateSharedLink] Response:', { data, error: err });

      if (err) {
        console.error('[useCreateSharedLink] Edge function error:', err);
        setError(err.message || 'unknown_error');
        return null;
      }

      if (!data?.success) {
        console.error('[useCreateSharedLink] Response error:', data);
        setError(data?.error || 'unknown_error');
        return null;
      }

      const linkData: SharedLinkData = {
        shareToken: data.shareToken,
        shareUrl: data.shareUrl,
      };
      
      setSharedLink(linkData);
      
      return linkData;
    } catch (e: any) {
      console.error('[useCreateSharedLink] Unexpected error:', e);
      setError(e.message || 'network_error');
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const reset = () => {
    setSharedLink(null);
    setError(null);
    setIsGenerating(false);
  };

  return { 
    generateLink, 
    isGenerating, 
    error, 
    sharedLink,
    reset
  };
};
